import { getApiErrorCode } from '../../../utils/apiError'

export const LEGAL_DOCUMENT_TYPE = Object.freeze({
  SERVICE_TERMS: 'SERVICE_TERMS',
  PRIVACY_POLICY: 'PRIVACY_POLICY',
  AGE_14_CONFIRMATION: 'AGE_14_CONFIRMATION',
  POSTHOG_ANALYTICS: 'POSTHOG_ANALYTICS',
  POSTHOG_OVERSEAS_TRANSFER: 'POSTHOG_OVERSEAS_TRANSFER',
  AI_LIMITATIONS: 'AI_LIMITATIONS',
  AI_PERSONAL_DATA: 'AI_PERSONAL_DATA',
  OPENAI_OVERSEAS_TRANSFER: 'OPENAI_OVERSEAS_TRANSFER',
})

export const LEGAL_INTERACTION_TYPE = Object.freeze({
  AGREEMENT: 'AGREEMENT',
  ACKNOWLEDGEMENT: 'ACKNOWLEDGEMENT',
  CONFIRMATION: 'CONFIRMATION',
  CONSENT: 'CONSENT',
})

export const LEGAL_ACTION_TYPE = Object.freeze({
  AGREED: 'AGREED',
  DECLINED: 'DECLINED',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  CONFIRMED: 'CONFIRMED',
  REVOKED: 'REVOKED',
})

export const LEGAL_ACTION_CONTEXT = Object.freeze({
  SIGN_UP: 'SIGN_UP',
  EXISTING_USER_ONBOARDING: 'EXISTING_USER_ONBOARDING',
  AI_FIRST_USE: 'AI_FIRST_USE',
  CONSENT_MANAGEMENT: 'CONSENT_MANAGEMENT',
  POLICY_UPDATE: 'POLICY_UPDATE',
})

export const LEGAL_DOCUMENT_ERROR = Object.freeze({
  DOCUMENT_NOT_FOUND: 'LEGAL_DOCUMENT_NOT_FOUND',
  CURRENT_DOCUMENT_NOT_FOUND: 'CURRENT_LEGAL_DOCUMENT_NOT_FOUND',
  INVALID_ACTION_FOR_CONTEXT: 'INVALID_LEGAL_DOCUMENT_ACTION_FOR_CONTEXT',
  REQUIRED_ACTION_MISSING: 'REQUIRED_LEGAL_DOCUMENT_ACTION_MISSING',
  REQUEST_CONFLICT: 'LEGAL_DOCUMENT_ACTION_REQUEST_CONFLICT',
})

const POSITIVE_ACTION_BY_INTERACTION = Object.freeze({
  [LEGAL_INTERACTION_TYPE.AGREEMENT]: LEGAL_ACTION_TYPE.AGREED,
  [LEGAL_INTERACTION_TYPE.ACKNOWLEDGEMENT]: LEGAL_ACTION_TYPE.ACKNOWLEDGED,
  [LEGAL_INTERACTION_TYPE.CONFIRMATION]: LEGAL_ACTION_TYPE.CONFIRMED,
  [LEGAL_INTERACTION_TYPE.CONSENT]: LEGAL_ACTION_TYPE.AGREED,
})

export const POSTHOG_DOCUMENT_TYPES = Object.freeze([
  LEGAL_DOCUMENT_TYPE.POSTHOG_ANALYTICS,
  LEGAL_DOCUMENT_TYPE.POSTHOG_OVERSEAS_TRANSFER,
])

export const AI_CONSENT_DOCUMENT_TYPES = Object.freeze([
  LEGAL_DOCUMENT_TYPE.AI_PERSONAL_DATA,
  LEGAL_DOCUMENT_TYPE.OPENAI_OVERSEAS_TRANSFER,
])

export const AI_REQUIRED_DOCUMENT_TYPES = Object.freeze([
  LEGAL_DOCUMENT_TYPE.AI_LIMITATIONS,
  ...AI_CONSENT_DOCUMENT_TYPES,
])

export const CONSENT_DOCUMENT_GROUPS = Object.freeze([
  {
    key: 'posthog',
    title: '서비스 분석정보',
    description: '서비스 이용 흐름과 오류를 분석해 사용성을 개선해요.',
    documentTypes: POSTHOG_DOCUMENT_TYPES,
  },
  {
    key: 'ai',
    title: 'AI 코칭 개인정보 처리',
    description: '학습 데이터를 바탕으로 AI 코칭을 제공하는 데 필요한 동의예요.',
    documentTypes: AI_CONSENT_DOCUMENT_TYPES,
  },
])

export function createLegalRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16)
    const value = character === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

export function getPositiveActionType(interactionType) {
  const actionType = POSITIVE_ACTION_BY_INTERACTION[interactionType]
  if (!actionType) throw new Error(`Unsupported legal interaction type: ${interactionType}`)
  return actionType
}

export function isPositiveLegalAction(header, action) {
  return Boolean(action)
    && action.legalDocumentId === header.legalDocumentId
    && action.actionType === getPositiveActionType(header.interactionType)
}

export function getLegalActionType({
  checked,
  header,
  previousActionType,
}) {
  if (checked) return getPositiveActionType(header.interactionType)
  if (header.interactionType !== LEGAL_INTERACTION_TYPE.CONSENT) return null
  return previousActionType === LEGAL_ACTION_TYPE.AGREED
    || previousActionType === LEGAL_ACTION_TYPE.REVOKED
    ? LEGAL_ACTION_TYPE.REVOKED
    : LEGAL_ACTION_TYPE.DECLINED
}

export function createLegalDocumentSelections(headers, actionsByDocumentId = {}) {
  return headers.reduce((selections, header) => ({
    ...selections,
    [header.legalDocumentId]: isPositiveLegalAction(
      header,
      actionsByDocumentId[header.legalDocumentId],
    ),
  }), {})
}

export function createLegalDocumentSelectionsFromStatuses(statuses = []) {
  return statuses.reduce((selections, status) => ({
    ...selections,
    [status.legalDocumentId]: isLegalDocumentRequirementSatisfied(status),
  }), {})
}

export function isLegalDocumentRequirementSatisfied(status) {
  return Boolean(status?.currentAction)
    && status.currentAction.legalDocumentId === status.legalDocumentId
    && status.currentAction.actionType === getPositiveActionType(status.interactionType)
}

export function isLegalConsentAgreed(status) {
  return status?.currentAction?.actionType === LEGAL_ACTION_TYPE.AGREED
}

export function areLegalDocumentTypesAgreed(statuses = [], documentTypes = []) {
  return documentTypes.every(documentType => {
    const status = statuses.find(item => item.documentType === documentType)
    return status && isLegalConsentAgreed(status)
  })
}

export function areLegalDocumentTypesSatisfied(statuses = [], documentTypes = []) {
  return documentTypes.every(documentType => {
    const status = statuses.find(item => item.documentType === documentType)
    return status && isLegalDocumentRequirementSatisfied(status)
  })
}

export function hasRequiredLegalActionStatuses(statuses = []) {
  return statuses
    .filter(status => !status.isOptional)
    .every(isLegalDocumentRequirementSatisfied)
}

export function hasRequiredLegalSelections(headers, selections) {
  return headers
    .filter(header => !header.isOptional)
    .every(header => Boolean(selections[header.legalDocumentId]))
}

export function buildLegalDocumentActions({
  headers,
  previousActionsByDocumentId = {},
  requestIdsByDocumentId = {},
  selections,
}) {
  return headers.map(header => {
    const legalDocumentId = header.legalDocumentId
    const actionType = getLegalActionType({
      checked: Boolean(selections[legalDocumentId]),
      header,
      previousActionType: previousActionsByDocumentId[legalDocumentId]?.actionType,
    })

    if (!actionType) return null

    return {
      requestId: requestIdsByDocumentId[legalDocumentId] || createLegalRequestId(),
      legalDocumentId,
      actionType,
    }
  }).filter(Boolean)
}

export function getLegalDocumentLabel(header) {
  const title = header.title.replace(/^akive\s*/i, '')
  switch (header.interactionType) {
    case LEGAL_INTERACTION_TYPE.AGREEMENT:
      return `${title}에 동의합니다.`
    case LEGAL_INTERACTION_TYPE.ACKNOWLEDGEMENT:
      return `${title}을 확인했습니다.`
    case LEGAL_INTERACTION_TYPE.CONFIRMATION:
      return title.endsWith('확인') ? title.slice(0, -2).trim() : title
    case LEGAL_INTERACTION_TYPE.CONSENT:
      return `${title.replace(/\s*동의$/, '')}에 동의합니다.`
    default:
      return title
  }
}

export function shouldRefreshLegalDocuments(error) {
  return [
    LEGAL_DOCUMENT_ERROR.INVALID_ACTION_FOR_CONTEXT,
    LEGAL_DOCUMENT_ERROR.REQUIRED_ACTION_MISSING,
    LEGAL_DOCUMENT_ERROR.DOCUMENT_NOT_FOUND,
    LEGAL_DOCUMENT_ERROR.CURRENT_DOCUMENT_NOT_FOUND,
  ].includes(getApiErrorCode(error))
}

export function isLegalActionRequestConflict(error) {
  return getApiErrorCode(error) === LEGAL_DOCUMENT_ERROR.REQUEST_CONFLICT
}
